/**
 * GFG-364 v2 — the Budget history modal's rows (spec B11): the server sends amendments oldest
 * first, the modal lists them newest first with each figure as "before → after".
 */

import { COPY } from "@/constants/campaignContacts";
import type { BudgetAmendmentHistoryRow } from "@/interfaces/campaignContacts";
import { formatUsd, formatUsdChange } from "@/utils/number-format";
import { formatContactDate } from "./campaignContacts-helper";
import type { BudgetHistoryRowView } from "./types";

const ARROW = " → ";

function change(before: string | null, after: string | null): string {
  return formatUsdChange(before, after);
}

function paymentLabel(payment: BudgetAmendmentHistoryRow["payment"]): string | null {
  if (!payment) return null;
  const reference = payment.reference ?? `#${payment.id}`;
  return payment.type === "invoice"
    ? COPY.budgetHistory.payment.invoice(reference)
    : COPY.budgetHistory.payment.purchase(reference);
}

function expiryLabel(
  expire: BudgetAmendmentHistoryRow["expireDate"],
  timeZone: string,
): string | null {
  if (expire.before === expire.after) return null;
  const before = formatContactDate(expire.before, timeZone);
  return `${before}${ARROW}${formatContactDate(expire.after, timeZone)}`;
}

function toView(row: BudgetAmendmentHistoryRow, timeZone: string): BudgetHistoryRowView {
  return {
    id: row.id,
    when: formatContactDate(row.amendedAt, timeZone),
    who: row.amendedBy.name ?? COPY.emptyValue,
    budget: change(row.approvedBudget.before, row.approvedBudget.after),
    deposit: row.deposit ? change(row.deposit.before, row.deposit.after) : null,
    recipients: row.recipientsAdded.toLocaleString(),
    credits: formatUsd(row.claimAdded),
    payment: paymentLabel(row.payment),
    expiry: expiryLabel(row.expireDate, timeZone),
  };
}

/**
 * Newest first, every figure formatted; the amendment date and the expiry are calendar days in the
 * campaign's `timeZone`. Does not reorder the input.
 */
export function budgetHistoryRows(
  rows: readonly BudgetAmendmentHistoryRow[],
  timeZone: string,
): BudgetHistoryRowView[] {
  return [...rows].reverse().map((row) => toView(row, timeZone));
}
